import React, { useEffect, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { authorizedFetch } from '../utils/api';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

function Card({ title, value, icon, tooltip }) {
  return (
    <div className="relative bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center min-h-[100px]">
      <div className="flex items-center gap-2 mb-1">
        {icon && <span className="text-2xl">{icon}</span>}
        <span className="text-gray-500 text-sm font-medium">{title}</span>
        {tooltip && (
          <span className="group relative">
            <InformationCircleIcon className="w-4 h-4 text-gray-400 ml-1 cursor-pointer" />
            <span className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
              {tooltip}
            </span>
          </span>
        )}
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

function Histogram({ title, bins, colorClass }) {
  const maxCount = Math.max(1, ...(bins || []).map((b) => b.count || 0));
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold mb-4">{title}</h2>
      <div className="space-y-2">
        {(bins || []).map((bin) => {
          const count = bin.count || 0;
          const pct = (count / maxCount) * 100;
          return (
            <div key={bin.bin} className="flex items-center gap-3">
              <div className="w-12 text-xs text-gray-600 shrink-0">{bin.bin}</div>
              <div className="flex-1 bg-gray-100 rounded h-6 overflow-hidden">
                <div
                  className={`h-6 ${colorClass}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-10 text-right text-sm font-medium text-gray-700 shrink-0">{count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const BRAND_COLUMNS = [
  { key: 'logo_url', label: '🏢 Company Logo', sortable: false },
  { key: 'brand', label: '🏷️ Fashion Brand', sortable: true },
  { key: 'latest_year', label: '🗓️ Latest Year', sortable: true },
  { key: 'first_year', label: '📆 First Year', sortable: true },
  { key: 'top_season', label: '🍂 Top Season', sortable: true },
  { key: 'total_looks', label: '📦 Total Looks', sortable: true },
  { key: 'evaluated_looks_human', label: '👩‍🦰 Evaluated by Human', sortable: true },
  { key: 'evaluated_looks_machine', label: '🤖 Evaluated by Machine', sortable: true },
  { key: 'avg_machine_score', label: '🤖 Avg Machine Score', sortable: true },
  { key: 'avg_buyer_score', label: '👩‍🦰 Avg Buyer Score', sortable: true },
];

function SortableTable({ data, onRowClick, selectedBrand }) {
  const [sortKey, setSortKey] = useState('total_looks');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = React.useMemo(() => {
    const sortedData = [...data];
    sortedData.sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sortedData;
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg shadow bg-white mt-10">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {BRAND_COLUMNS.map((col) => (
              <th
                key={col.key}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-700 cursor-pointer select-none group"
                onClick={() => col.sortable && handleSort(col.key)}
              >
                <span className="flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortKey === col.key && (
                    sortDir === 'asc' ? (
                      <ChevronUpIcon className="w-4 h-4 text-blue-500" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-blue-500" />
                    )
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={BRAND_COLUMNS.length} className="text-center py-8 text-gray-400">
                No brand data available.
              </td>
            </tr>
          ) : (
            sorted.map((row, i) => (
              <tr
                key={row.brand + i}
                className={`transition ${onRowClick ? 'cursor-pointer' : ''} ${
                  selectedBrand && selectedBrand === row.brand ? 'bg-blue-50' : 'hover:bg-blue-50'
                }`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                <td className="px-4 py-2">
                  <div className="flex justify-center" title={row.brand}>
                    {row.logo_url ? (
                      <img
                        src={row.logo_url}
                        alt={`${row.brand} logo`}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                        {(row.brand || '?').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{row.brand}</td>
                <td className="px-4 py-2 text-right">{row.latest_year ?? 'N/A'}</td>
                <td className="px-4 py-2 text-right">{row.first_year ?? 'N/A'}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.top_season ?? 'N/A'}</td>
                <td className="px-4 py-2 text-right">{row.total_looks}</td>
                <td className="px-4 py-2 text-right">{row.evaluated_looks_human}</td>
                <td className="px-4 py-2 text-right">{row.evaluated_looks_machine}</td>
                <td className="px-4 py-2 text-right">{row.avg_machine_score ?? 'N/A'}</td>
                <td className="px-4 py-2 text-right">{row.avg_buyer_score ?? 'N/A'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Task() {
  const [stats, setStats] = useState(null);
  const [brandSummary, setBrandSummary] = useState([]);
  const [brandStats, setBrandStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [yearlyTrend, setYearlyTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      authorizedFetch('/fashion-looks/task-summary').then(res => {
        if (!res.ok) throw new Error('Failed to fetch task summary');
        return res.json();
      }),
      authorizedFetch('/fashion-looks/brand_summary').then(res => {
        if (!res.ok) throw new Error('Failed to fetch brand summary');
        return res.json();
      }),
      authorizedFetch('/fashion-looks/brand_stats').then(res => {
        if (!res.ok) throw new Error('Failed to fetch brand stats');
        return res.json();
      })
    ])
      .then(([statsData, brandSummaryData, brandStatsData]) => {
        setStats(statsData);
        setBrandSummary(brandSummaryData);
        setBrandStats(brandStatsData);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <Spinner />;
  if (error) return <div className="p-8 text-center text-red-500">Error: {error}</div>;
  if (!stats || !brandStats) return null;

  const { overall } = stats;

  const handleBrandRowClick = async (row) => {
    const brand = row?.brand;
    if (!brand) return;
    setSelectedBrand(brand);
    setTrendLoading(true);
    setTrendError(null);
    try {
      const resp = await authorizedFetch(`/fashion-looks/brand-yearly-avg?brand=${encodeURIComponent(brand)}`);
      if (!resp.ok) throw new Error(`Failed to fetch yearly trend for ${brand}`);
      const data = await resp.json();
      setYearlyTrend(Array.isArray(data) ? data : []);
    } catch (err) {
      setTrendError(err.message || 'Failed to load yearly trend.');
      setYearlyTrend([]);
    } finally {
      setTrendLoading(false);
    }
  };

  return (
    <div className="w-full p-2 sm:p-4 animate-fade-in bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">📊 Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <Card
          title="Total Brands"
          value={brandStats.total_brands}
          icon="🏷️"
          tooltip="Total unique brands in all collections."
        />
        <Card
          title="Collections Analyzed"
          value={brandStats.total_collections}
          icon="📚"
          tooltip="Total number of collections across all brands. A brand can have multiple collections."
        />
        <Card
          title="Lines of Code"
          value={3656}
          icon="📏"
          tooltip="Total number of lines implemented in the frontend and backend core code."
        />
        <Card
          title="Brands Added Today"
          value={brandStats.brands_added_today}
          icon="🆕"
          tooltip="Brands whose first collection was created today (UTC)."
        />
        <Card
          title="Collections Added Today"
          value={brandStats.collections_added_today}
          icon="📅"
          tooltip="Number of collections created today (UTC), regardless of brand."
        />
        <Card
          title="Collections to Crawl"
          value={brandStats.collections_to_crawl}
          icon="🕸️"
          tooltip="Number of collections that still need to be crawled."
        />
        <Card
          title="Looks Failed to Scrape"
          value={brandStats.failed_scrape_looks ?? 0}
          icon="⚠️"
          tooltip="Expected looks in collection metadata that are still missing from scraped looks."
        />
        <Card
          title="Brands Analyzed"
          value={brandStats.analyzed_brands}
          icon="📊"
          tooltip="Brands for which looks have been parsed and analyzed."
        />
        <Card
          title="Brands with Collections"
          value={brandStats.brands_with_collections}
          icon="📥"
          tooltip="Brands with at least one collection (non-null id)."
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">
        <Card title="Total Looks" value={overall.total_looks} tooltip="Total number of fashion looks in the database." icon="#️⃣" />
        <Card title="Evaluated by Human" value={overall.evaluated_looks_human} tooltip="Looks that have been scored by a human buyer (buyer_score set)." icon="🧑‍⚖️" />
        <Card title="Evaluated by Machine" value={overall.evaluated_looks_machine} tooltip="Looks that have been scored by the machine (machine_score set)." icon="🤖" />
        <Card title="Not Evaluated" value={overall.not_evaluated_looks} tooltip="Looks that have not been evaluated by either human or machine." icon="❓" />
        <Card title="Avg. Machine Score" value={overall.avg_machine_score ?? 'N/A'} tooltip="Average machine-generated score for all looks." icon="📈" />
        <Card title="Avg. Buyer Score" value={overall.avg_buyer_score ?? 'N/A'} tooltip="Average human buyer score for all looks." icon="📊" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Histogram
          title="Histogram of buyer_score"
          bins={overall.buyer_score_histogram || []}
          colorClass="bg-blue-500"
        />
        <Histogram
          title="Histogram of machine_score"
          bins={overall.machine_score_histogram || []}
          colorClass="bg-emerald-500"
        />
      </div>
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Fashion Brand Leader Board</h2>
        <SortableTable
          data={brandSummary}
          onRowClick={handleBrandRowClick}
          selectedBrand={selectedBrand}
        />
        {selectedBrand && (
          <div className="mt-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">
                {selectedBrand} Score Trend by Year
              </h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedBrand(null);
                  setYearlyTrend([]);
                  setTrendError(null);
                }}
                className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            </div>

            {trendLoading && <div className="text-sm text-gray-600">Loading yearly trend...</div>}
            {!trendLoading && trendError && <div className="text-sm text-red-500">{trendError}</div>}
            {!trendLoading && !trendError && yearlyTrend.length === 0 && (
              <div className="text-sm text-gray-500">No yearly score data available for this brand.</div>
            )}
            {!trendLoading && !trendError && yearlyTrend.length > 0 && (
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yearlyTrend} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="avg_buyer_score" name="Avg Buyer Score" stroke="#2563eb" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="avg_machine_score" name="Avg Machine Score" stroke="#059669" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
